import {
    UpdateCareRecipientRequestType,
    deleteCareRecipient,
    getCareRecipientInfo,
    getCareRecipientList,
    registerCareRecipient,
    updateCareRecipient,
    getAllAlerts,
    updatePeriod,
    disableAlert,
    disableAlerts,
    toggleAlertDisplay
} from "./api";
import { CareRecipientId } from "../../../../domain/care_recipient/CareRecipientId";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { orderErrorToast, orderSuccessToast } from "../../../../app/funcs/orderResultToast";
import { CareRecipientSortCondition } from "../../../../domain/care_recipient/CareRecipientWithDataSyncInformation";

export const ActionType = Object.freeze({
    base: "@@Monitoring/Action/Admin/CareRecipient",
    getList: "getList",
    keywordSearch: "keywordSearch",
    hideDataSyncFinished: "hideDataSyncFinished",
    sort: "sort",
    getInfo: "getInfo",
    register: "register",
    update: "update",
    delete: "delete",
    getAlerts: "getAlerts",
    setPage: "setPage",
    updatePeriod: "updatePeriod",
    getMonth: "getMonth",
    checkDisableAlert: "checkDisableAlert",
    toggleAllCheckAlerts: "toggleAllCheckAlert",
    disableAlert: "disableAlert",
    disableAlerts: "disableAlerts",
    toggleAlertDisplay: "toggleAlertDisplay",
});

export const getCareRecipientListAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getList}`,
    async(_, { rejectWithValue }) => {
        const response = await getCareRecipientList();
        console.log(response);

        if (response.error === undefined) {
            // API処理結果正常の場合
            return { ...response };
        } else {
            // API処理結果異常の場合
            return rejectWithValue({});
        }
    }
);

export const keywordSearchAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.keywordSearch}`,
    async(data: {
        searchKeyword: string
    }) => data
);

export const hideDataSyncFinishedAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.hideDataSyncFinished}`,
    async(data: {
        hideDataSyncFinished: boolean
    }) => data
);

export const sortAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.sort}`,
    async(data: {
        sortCondition: CareRecipientSortCondition,
        asc: boolean
    }) => data
);

export const getCareRecipientInfoAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getInfo}`,
    async(careRecipientId: CareRecipientId, { rejectWithValue }) => {
        const response = await getCareRecipientInfo(careRecipientId);

        if (response.error === undefined) {
            // API処理結果正常の場合
            return { careRecipientInfo: response };
        } else {
            // API処理結果異常の場合
            return rejectWithValue({});
        }
    }
);

export const updateCareRecipientInfoAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.update}`,
    async(data: {
              careRecipientId: CareRecipientId,
              requestData: UpdateCareRecipientRequestType,
        onFailure: (errorCode: string) => void
    }, { rejectWithValue }) => {
        const response = await updateCareRecipient(data.careRecipientId, data.requestData);

        if (response.error === undefined) {
            // API処理結果正常の場合
            orderSuccessToast("利用者を更新しました");
            return { careRecipientInfo: response };
        } else {
            // API処理結果異常の場合
            orderErrorToast("利用者の更新に失敗しました");
            data.onFailure(response.error.code);
            return rejectWithValue({});
        }
    }
);

export const registerCareRecipientInfoAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.register}`,
    async(data: {
              requestData: UpdateCareRecipientRequestType,
        onFailure: () => void
    }, { rejectWithValue }) => {
        const response = await registerCareRecipient(data.requestData);

        if (response.error === undefined) {
            // API処理結果正常の場合
            orderSuccessToast("利用者を登録しました");
            return { careRecipientInfo: response };
        } else {
            // API処理結果異常の場合
            orderErrorToast("利用者の登録に失敗しました");
            data.onFailure();
            return rejectWithValue({});
        }
    }
);

export const deleteCareRecipientAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.delete}`,
    async(data: {
        careRecipientId: CareRecipientId
    }, { rejectWithValue }) => {
        const response  = await deleteCareRecipient(data.careRecipientId);

        if (response.error === undefined) {
            // API処理結果正常の場合
            orderSuccessToast("利用者を削除しました");
            return { careRecipientId: data.careRecipientId.value };
        } else {
            // API処理結果異常の場合
            orderErrorToast("利用者の削除に失敗しました");
            return rejectWithValue({});
        }
    }
);

export const getAllAlertsAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.getAlerts}`,
    async(_: {}, { rejectWithValue }) => {
        const response = await getAllAlerts();
        if (response.error === undefined) {
            return response;
        } else {
            return rejectWithValue({});
        }
    }
);

export const setPageAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.setPage}`,
    async(data: { page: number }) => data
);

export const updatePeriodAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.updatePeriod}`,
    async(data: { careRecipientId: string, startDate: string | null, endDate: string | null}, { rejectWithValue }) => {
        const response = await updatePeriod(data.careRecipientId, data.startDate, data.endDate);
        if (response.error === undefined) {
            return response;
        } else {
            return rejectWithValue({});
        }
    }
);

export const checkDisableAlertAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.checkDisableAlert}`,
    async(data: { alertId: string }) => data
);

export const toggleAllCheckAlertsAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.toggleAllCheckAlerts}`,
    async(data: { check: boolean }) => data
);

export const disableAlertAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.disableAlert}`,
    async(data: {
        alertId: string
    }) => {
        disableAlert(data.alertId);
        return { alertId: data.alertId };
    }
);

export const disableAlertsAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.disableAlerts}`,
    async(data: {
        alertIdList: string[]
    }) => {
        disableAlerts(data.alertIdList);
        return { alertIdList: data.alertIdList };
    }
);

export const toggleAlertDisplayAction = createAsyncThunk(
    `${ActionType.base}/${ActionType.toggleAlertDisplay}`,
    async(data: {
        alertId: string
    }) => {
        toggleAlertDisplay(data.alertId);
        return { alertId: data.alertId };
    }
);
